import { IcInfo16 } from '@assets/icons/16x16';
import { Box, Button, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import ErrorsContainer from '@components/common/form/ErrorsContainer';
import { DraggablePanel } from '@components/ui/draggable';
import { createToast, ToastStatusEnum } from '@components/ui/toaster';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectActiveUser } from '@redux/authent/authent.selectors';
import { useResetSituationMutation } from '@services/c2/c2.api';
import { createErrorMessage } from '@utils/common.utils';
import { FormattedMessage, useIntl } from 'react-intl';

import { PopupPositionData } from '@/types/popup.types';

import PopupContainer from './common/PopupContainer';

export default function ResetSituationPopup({ mapConstraintsRef, top, left, onClose }: Readonly<PopupPositionData>) {
  const { formatMessage } = useIntl();
  const [resetSituation, { isLoading, isError, error }] = useResetSituationMutation();

  const { activeSite } = useAppSelector(selectActiveUser);

  return (
    <DraggablePanel.Root constraintsRef={mapConstraintsRef} top={top} left={left}>
      <DraggablePanel.Header onClose={onClose}>
        <FormattedMessage id="situationReset.title" />
      </DraggablePanel.Header>
      <DraggablePanel.Body>
        <PopupContainer>
          <VStack backgroundColor="neutral.700" width="100%" height="100%" gap={3} alignItems="start" userSelect="none">
            <Text width="100%" textStyle="lg" fontWeight="medium" whiteSpace="pre-line">
              <FormattedMessage id="situationReset.description" />
            </Text>
            <HStack gap={2}>
              <Icon as={IcInfo16} color="neutral.white" />
              <Text textStyle="md">
                <FormattedMessage id="global.unrecoverableAction" />
              </Text>
            </HStack>
            {isError && (
              <Box height="40px">
                <ErrorsContainer errors={{}} touched={{}} apiError={createErrorMessage(error)} />
              </Box>
            )}
            <HStack width="100%" gap={2} justifyContent="center">
              <Box borderWidth="2px" borderColor="blue.700">
                <Button variant="secondary" textStyle="lg" onClick={onClose}>
                  <FormattedMessage id="global.cancel" />
                </Button>
              </Box>
              <Box borderWidth="2px" borderColor="blue.700">
                <Button
                  disabled={isLoading || isError}
                  loading={isLoading}
                  onClick={() =>
                    activeSite &&
                    resetSituation([activeSite.code])
                      .unwrap()
                      .then(() => {
                        createToast(formatMessage({ id: 'situationReset.successToast' }), ToastStatusEnum.SUCCESS);
                        onClose();
                      })
                  }
                >
                  <FormattedMessage id="situationReset.confirm" />
                </Button>
              </Box>
            </HStack>
          </VStack>
        </PopupContainer>
      </DraggablePanel.Body>
    </DraggablePanel.Root>
  );
}
